import React, { useEffect, useState, useRef } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import { apiClient } from "../../../../services/apiClient";
import { ATP_API_URL } from "../../../../config/apiConfig";
import "./index.scss";

// ── File Type & MIME Helpers ──────────────────────────────────────────────
const IMAGE_EXTENSIONS = new Set([
  ".png",
  ".jpg",
  ".jpeg",
  ".webp",
  ".gif",
  ".svg",
  ".bmp",
  ".ico",
  ".tiff",
  ".tif",
  ".avif",
]);

const EXT_TO_MIME = {
  // Images
  ".png": "image/png",
  ".jpg": "image/jpeg",
  ".jpeg": "image/jpeg",
  ".webp": "image/webp",
  ".gif": "image/gif",
  ".svg": "image/svg+xml",
  ".bmp": "image/bmp",
  ".ico": "image/x-icon",
  ".tiff": "image/tiff",
  ".tif": "image/tiff",
  ".avif": "image/avif",
  // PDF
  ".pdf": "application/pdf",
  // Documents
  ".doc": "application/msword",
  ".docx":
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ".xls": "application/vnd.ms-excel",
  ".xlsx":
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ".ppt": "application/vnd.ms-powerpoint",
  ".pptx":
    "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  ".csv": "text/csv",
  ".txt": "text/plain",
};

const getExt = (doc) => {
  let ext = (doc?.fileType || "").trim().toLowerCase();
  if (!ext && doc?.fileName) {
    const parts = doc.fileName.split(".");
    if (parts.length > 1) {
      ext = "." + parts.pop().toLowerCase();
    }
  }
  if (ext && !ext.startsWith(".")) {
    ext = "." + ext;
  }
  return ext;
};

const isImageFile = (ext, mime) => {
  if (mime && mime.startsWith("image/")) return true;
  return IMAGE_EXTENSIONS.has(ext);
};

const isPdfFile = (ext, mime) => {
  if (mime && mime.includes("pdf")) return true;
  return ext === ".pdf";
};

/**
 * UploadedDocumentsView
 *
 * Shown only when entity.folderNo exists.
 *
 * 1. GET BULK_UPLOAD_DOCUMENT_VIEW_ALL?folderNo=...
 * 2. For each doc.id → GET VIEW_BULK_UPLOAD_DOCUMENT/{id}  (binary image/pdf/doc)
 *
 * Usage:
 *   {entity?.folderNo && (
 *     <UploadedDocumentsView folderNo={entity.folderNo} />
 *   )}
 *   // or
 *   <UploadedDocumentsView entityPrimaryId={entity.folderNo} />
 */
const UploadedDocumentsView = ({ folderNo, entityPrimaryId }) => {
  const effectiveFolderNo = folderNo || entityPrimaryId;

  const [state, setState] = useState("idle"); // idle | loading | loaded | error
  const [documents, setDocuments] = useState([]);
  const [errMsg, setErrMsg] = useState("");
  const blobUrlsRef = useRef([]);

  const revokeAllBlobs = () => {
    blobUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    blobUrlsRef.current = [];
  };

  useEffect(() => {
    if (!effectiveFolderNo) return;

    let cancelled = false;

    const load = async () => {
      setState("loading");
      setDocuments([]);
      setErrMsg("");
      revokeAllBlobs();

      try {
        // 1. Get document list
        const listRes = await apiClient.get(
          `${ATP_API_URL.BULK_UPLOAD_DOCUMENT_VIEW_ALL}?folderNo=${effectiveFolderNo}`,
          { validateStatus: () => true },
        );

        if (cancelled) return;

        if (listRes?.status !== 200 || !listRes.data?.success) {
          setErrMsg(
            listRes?.data?.message ||
              `Failed to load documents (HTTP ${listRes?.status})`,
          );
          setState("error");
          return;
        }

        const items = listRes.data?.data || [];
        if (items.length === 0) {
          setDocuments([]);
          setState("loaded");
          return;
        }

        // 2. Fetch real document file for every id (images, PDF, and all other types)
        const loadedDocs = await Promise.all(
          items.map(async (doc) => {
            try {
              const imgRes = await apiClient.get(
                `${ATP_API_URL.VIEW_BULK_UPLOAD_DOCUMENT}/${doc.id}`,
                {
                  responseType: "blob",
                  validateStatus: () => true,
                },
              );

              if (cancelled) return null;

              if (imgRes?.status === 200 && imgRes.data) {
                const ext = getExt(doc);
                const headerType =
                  imgRes.headers?.["content-type"] ||
                  imgRes.data?.type ||
                  "";
                const mime =
                  (headerType &&
                  headerType !== "application/octet-stream" &&
                  !headerType.includes("application/json")
                    ? headerType
                    : EXT_TO_MIME[ext]) ||
                  (isImageFile(ext)
                    ? "image/jpeg"
                    : isPdfFile(ext)
                      ? "application/pdf"
                      : "application/octet-stream");

                const blob = new Blob([imgRes.data], { type: mime });
                const url = URL.createObjectURL(blob);
                blobUrlsRef.current.push(url);

                return {
                  ...doc,
                  imgUrl: url,
                  fileExt: ext,
                  mime,
                  isImage: isImageFile(ext, mime),
                  isPdf: isPdfFile(ext, mime),
                };
              }

              return { ...doc, imgUrl: null, error: true };
            } catch {
              return { ...doc, imgUrl: null, error: true };
            }
          }),
        );

        if (cancelled) return;

        setDocuments(loadedDocs.filter(Boolean));
        setState("loaded");
      } catch (err) {
        if (!cancelled) {
          setErrMsg(err?.message || "Failed to load documents");
          setState("error");
        }
      }
    };

    load();

    return () => {
      cancelled = true;
      revokeAllBlobs();
    };
  }, [effectiveFolderNo]);

  if (!effectiveFolderNo) return null;

  const openFullscreen = (url) => {
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="udv">
      {/* Header */}
      <div className="udv__header">
        <div className="udv__header-left">
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
          </svg>
          <span className="udv__title">Uploaded Documents</span>
          <span className="udv__badge">
            {effectiveFolderNo}
            {state === "loaded" && documents.length > 0
              ? ` · ${documents.length}`
              : ""}
          </span>
        </div>
      </div>

      {/* Body */}
      <div className="udv__body">
        {state === "loading" && (
          <div className="udv__loading">
            <CircularProgress
              size={30}
              thickness={4}
              sx={{ color: "#2563eb" }}
            />
            <p>Loading documents…</p>
          </div>
        )}

        {state === "error" && (
          <div className="udv__error">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#ef4444"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <p>{errMsg}</p>
          </div>
        )}

        {state === "loaded" && documents.length === 0 && (
          <div className="udv__empty">
            <p>No documents uploaded for this folder.</p>
          </div>
        )}

        {state === "loaded" && documents.length > 0 && (
          <div className="udv__grid">
            {documents.map((doc) => (
              <div className="udv__card" key={doc.id}>
                <div
                  className="udv__card-preview"
                  onClick={() => doc.imgUrl && openFullscreen(doc.imgUrl)}
                >
                  {doc.imgUrl && doc.isImage ? (
                    <img
                      className="udv__card-img"
                      src={doc.imgUrl}
                      alt={doc.fileName || "Uploaded document"}
                    />
                  ) : doc.imgUrl && doc.isPdf ? (
                    <div className="udv__card-pdf-wrap">
                      <iframe
                        className="udv__card-iframe"
                        src={`${doc.imgUrl}#toolbar=0&navpanes=0`}
                        title={doc.fileName || "PDF document"}
                      />
                      <div className="udv__card-pdf-badge">PDF</div>
                    </div>
                  ) : (
                    <div className="udv__card-fallback">
                      <svg
                        width="30"
                        height="30"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.75"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="udv__card-fallback-icon"
                      >
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                        <line x1="16" y1="13" x2="8" y2="13" />
                        <line x1="16" y1="17" x2="8" y2="17" />
                        <polyline points="10 9 9 9 8 9" />
                      </svg>
                      <span className="udv__card-fallback-ext">
                        {(doc.fileExt || doc.fileType || "FILE")
                          .replace(/^\./, "")
                          .toUpperCase()}
                      </span>
                    </div>
                  )}

                  {doc.imgUrl && (
                    <button
                      className="udv__card-fs"
                      onClick={(e) => {
                        e.stopPropagation();
                        openFullscreen(doc.imgUrl);
                      }}
                      title="Open fullscreen"
                    >
                      <svg
                        width="11"
                        height="11"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="15 3 21 3 21 9" />
                        <polyline points="9 21 3 21 3 15" />
                        <line x1="21" y1="3" x2="14" y2="10" />
                        <line x1="3" y1="21" x2="10" y2="14" />
                      </svg>
                    </button>
                  )}
                </div>

                <div className="udv__card-footer">
                  <span className="udv__card-name" title={doc.fileName}>
                    {doc.fileName || `Document #${doc.id}`}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default UploadedDocumentsView;
